import * as THREE from 'three';
import { Weapon, WeaponConfig } from './Weapon';
import { EventBus } from '../core/EventBus';

const MISSILE_CONFIG: WeaponConfig = {
  name: 'Homing Missiles',
  damage: 60,
  fireRate: 2, // 2 missiles per second
  projectileSpeed: 60,
  ammoMax: 24,
  reloadTime: 3,
  spread: 0.1,
  automatic: true,
  projectileColor: new THREE.Color(0xff6600),
  projectileSize: 0.25,
};

interface HomingMissile {
  mesh: THREE.Group;
  direction: THREE.Vector3;
  speed: number;
  damage: number;
  spawnTime: number;
  lifetime: number;
  target: THREE.Object3D | null;
  lastSmokeTime: number;
}

export class HomingMissiles extends Weapon {
  private scene: THREE.Scene;
  private missiles: HomingMissile[] = [];
  private targets: THREE.Object3D[] = [];
  private smokeParticles: THREE.Mesh[] = [];

  private readonly MISSILE_LIFETIME = 5;
  private readonly TURN_RATE = 3; // Radians per second
  private readonly LOCK_RANGE = 150;
  private readonly SMOKE_INTERVAL = 0.05;

  constructor(scene: THREE.Scene) {
    super(MISSILE_CONFIG);
    this.scene = scene;
  }

  public setTargets(targets: THREE.Object3D[]): void {
    this.targets = targets;
  }

  protected createProjectile(origin: THREE.Vector3, direction: THREE.Vector3): void {
    // Create missile mesh
    const group = new THREE.Group();

    // Missile body (elongated)
    const bodyGeometry = new THREE.CylinderGeometry(0.1, 0.15, 0.8, 8);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x666666,
      metalness: 0.8,
      roughness: 0.3,
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.rotation.x = Math.PI / 2;
    group.add(body);

    // Nose cone
    const noseGeometry = new THREE.ConeGeometry(0.1, 0.3, 8);
    const noseMaterial = new THREE.MeshStandardMaterial({
      color: 0xff6600,
      emissive: 0xff3300,
      emissiveIntensity: 0.5,
    });
    const nose = new THREE.Mesh(noseGeometry, noseMaterial);
    nose.rotation.x = -Math.PI / 2;
    nose.position.z = 0.55;
    group.add(nose);

    // Fins
    const finGeometry = new THREE.BoxGeometry(0.3, 0.02, 0.2);
    const finMaterial = new THREE.MeshStandardMaterial({ color: 0x444444 });
    for (let i = 0; i < 4; i++) {
      const fin = new THREE.Mesh(finGeometry, finMaterial);
      fin.rotation.z = (i * Math.PI) / 2;
      fin.position.z = -0.3;
      group.add(fin);
    }

    // Engine glow
    const engineGeometry = new THREE.SphereGeometry(0.12, 8, 8);
    const engineMaterial = new THREE.MeshBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.8,
    });
    const engine = new THREE.Mesh(engineGeometry, engineMaterial);
    engine.position.z = -0.4;
    group.add(engine);

    group.position.copy(origin);

    // Point missile in direction
    const lookAt = origin.clone().add(direction);
    group.lookAt(lookAt);

    this.scene.add(group);

    // Find nearest target
    const target = this.findNearestTarget(origin);

    this.missiles.push({
      mesh: group,
      direction: direction.clone().normalize(),
      speed: this.getEffectiveProjectileSpeed(),
      damage: this.getEffectiveDamage(),
      spawnTime: performance.now() / 1000,
      lifetime: this.MISSILE_LIFETIME,
      target,
      lastSmokeTime: 0,
    });
  }

  private findNearestTarget(origin: THREE.Vector3): THREE.Object3D | null {
    let nearest: THREE.Object3D | null = null;
    let nearestDist = this.LOCK_RANGE;

    for (const target of this.targets) {
      if (!target.userData.health || target.userData.health <= 0) continue;

      const dist = origin.distanceTo(target.position);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = target;
      }
    }

    return nearest;
  }

  public update(): void {
    super.update();

    const currentTime = performance.now() / 1000;
    const dt = 1 / 60;

    for (let i = this.missiles.length - 1; i >= 0; i--) {
      const missile = this.missiles[i];

      // Check lifetime
      if (currentTime - missile.spawnTime > missile.lifetime) {
        this.destroyMissile(i);
        continue;
      }

      // Update target if current one is dead
      if (missile.target && (!missile.target.userData.health || missile.target.userData.health <= 0)) {
        missile.target = this.findNearestTarget(missile.mesh.position);
      }

      // Home toward target
      if (missile.target) {
        const toTarget = missile.target.position.clone().sub(missile.mesh.position).normalize();
        const dot = missile.direction.dot(toTarget);

        if (dot < 0.99) {
          // Gradually turn toward target
          const cross = new THREE.Vector3().crossVectors(missile.direction, toTarget);
          const turnAmount = Math.min(this.TURN_RATE * dt, Math.acos(dot));

          missile.direction.applyAxisAngle(cross.normalize(), turnAmount);
          missile.direction.normalize();
        }
      }

      // Move missile
      missile.mesh.position.add(missile.direction.clone().multiplyScalar(missile.speed * dt));

      // Update rotation
      const lookAt = missile.mesh.position.clone().add(missile.direction);
      missile.mesh.lookAt(lookAt);

      // Spawn smoke trail
      if (currentTime - missile.lastSmokeTime > this.SMOKE_INTERVAL) {
        this.spawnSmoke(missile.mesh.position.clone());
        missile.lastSmokeTime = currentTime;
      }

      // Check ground collision
      if (missile.mesh.position.y < 0) {
        this.explode(missile, i);
        continue;
      }

      // Check target collisions
      for (const target of this.targets) {
        if (!target.userData.health) continue;

        const dist = missile.mesh.position.distanceTo(target.position);
        const hitRadius = (target.userData.hitRadius as number) || 1;

        if (dist < hitRadius + 0.5) {
          this.explode(missile, i);
          break;
        }
      }
    }

    // Update smoke particles
    this.updateSmoke(dt);
  }

  private spawnSmoke(position: THREE.Vector3): void {
    const geometry = new THREE.SphereGeometry(0.2, 6, 6);
    const material = new THREE.MeshBasicMaterial({
      color: 0x888888,
      transparent: true,
      opacity: 0.6,
    });
    const smoke = new THREE.Mesh(geometry, material);
    smoke.position.copy(position);
    smoke.userData.spawnTime = performance.now() / 1000;
    smoke.userData.lifetime = 0.5;
    this.scene.add(smoke);
    this.smokeParticles.push(smoke);
  }

  private updateSmoke(dt: number): void {
    const currentTime = performance.now() / 1000;

    for (let i = this.smokeParticles.length - 1; i >= 0; i--) {
      const smoke = this.smokeParticles[i];
      const age = currentTime - smoke.userData.spawnTime;

      if (age >= smoke.userData.lifetime) {
        this.scene.remove(smoke);
        smoke.geometry.dispose();
        (smoke.material as THREE.Material).dispose();
        this.smokeParticles.splice(i, 1);
        continue;
      }

      // Expand and fade
      const t = age / smoke.userData.lifetime;
      smoke.scale.setScalar(1 + t * 2);
      (smoke.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - t);
      smoke.position.y += dt * 2; // Float upward
    }
  }

  private explode(missile: HomingMissile, index: number): void {
    const center = missile.mesh.position.clone();

    // Damage target if hit directly
    for (const target of this.targets) {
      if (!target.userData.health) continue;

      const dist = center.distanceTo(target.position);
      const hitRadius = (target.userData.hitRadius as number) || 1;

      if (dist < hitRadius + 1) {
        const entity = target.userData.entity;
        if (entity && typeof entity.takeDamage === 'function') {
          entity.takeDamage(missile.damage);
        } else {
          target.userData.health = Math.max(0, target.userData.health - missile.damage);
          EventBus.emit('target:damaged', {
            id: target.userData.id,
            damage: missile.damage,
            healthRemaining: target.userData.health,
          });
        }
        break;
      }
    }

    // Create explosion visual
    this.createExplosion(center);

    EventBus.emit('projectile:hit', {
      position: { x: center.x, y: center.y, z: center.z },
      targetType: 'explosion',
    });

    this.destroyMissile(index);
  }

  private createExplosion(center: THREE.Vector3): void {
    const geometry = new THREE.SphereGeometry(1, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.8,
    });
    const explosion = new THREE.Mesh(geometry, material);
    explosion.position.copy(center);
    this.scene.add(explosion);

    const startTime = performance.now();
    const duration = 200;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const t = elapsed / duration;

      if (t >= 1) {
        this.scene.remove(explosion);
        explosion.geometry.dispose();
        (explosion.material as THREE.Material).dispose();
        return;
      }

      explosion.scale.setScalar(1 + t * 3);
      material.opacity = 0.8 * (1 - t);

      requestAnimationFrame(animate);
    };
    animate();
  }

  private destroyMissile(index: number): void {
    const missile = this.missiles[index];
    this.scene.remove(missile.mesh);

    // Dispose all children
    missile.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });

    this.missiles.splice(index, 1);
  }

  public destroy(): void {
    for (let i = this.missiles.length - 1; i >= 0; i--) {
      this.destroyMissile(i);
    }

    for (const smoke of this.smokeParticles) {
      this.scene.remove(smoke);
      smoke.geometry.dispose();
      (smoke.material as THREE.Material).dispose();
    }
    this.smokeParticles = [];
  }
}
